import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classnames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { iconChevronDown } from '@sorare/core/src/atoms/icons/system/iconChevronDown.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { Variant } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { LineupRewardsDistanceLabel } from '#src/components/lineup/LineupRewardsDistanceLabel/index.tsx';
import { ClickableAnyRewardConfig } from '#src/components/rewards/ClickableAnyRewardConfig/index.tsx';

import type {
  LineupRewardsDistancesQuery,
  LineupRewardsDistancesQueryVariables,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 0 })`
  background: var(--c-nd-50);
`;

const StyledSBHorizontal = styled(SBHorizontal)`
  padding-right: var(--unit);
`;

const StyledIconButton = styled(IconButton)`
  @media ${laptopAndAbove} {
    display: none;
  }
`;

const Loader = styled(Vertical).attrs({ gap: 0 })`
  min-height: 300px;
`;

const RewardConfig = styled(SBHorizontal).attrs({ gap: 2 })`
  border-top: 1px solid var(--c-nd-100);
  padding: var(--half-unit) var(--unit);
  font: var(--t-12);

  &.eligible {
    background-color: rgba(var(--c-rgb-blue-400), 0.1);
  }
`;

const PercentageOfGamesPlayed = styled(LabelL).attrs({ as: 'div', bold: true })`
  padding: var(--double-unit);
`;

const LINEUP_REWARDS_DISTANCES_QUERY = gql`
  query LineupRewardsDistancesQuery($so5LeaderboardContenderSlug: String!) {
    so5 {
      so5LeaderboardContender(slug: $so5LeaderboardContenderSlug) {
        slug
        so5Lineup {
          id
          so5Rankings {
            id
            score
            ranking
          }
        }
        so5Leaderboard {
          slug
          so5LeaderboardGroup {
            slug
            percentageOfGamesPlayed
          }
          so5League {
            slug
            ...ClickableAnyRewardConfig_so5League
          }
          rewardsConfig {
            ranking {
              id
              fromRank
              toRank
              toSo5Ranking {
                id
                score
              }
              rewardConfigs {
                id
                boostedRewardConfigs {
                  id
                  ...RewardConfigText_anyRewardConfigInterface
                }
                ...ClickableAnyRewardConfig_anyRewardConfigInterface
              }
            }
            conditional {
              id
              score
              rewardConfigs {
                id
                boostedRewardConfigs {
                  id
                  ...RewardConfigText_anyRewardConfigInterface
                }
                ...ClickableAnyRewardConfig_anyRewardConfigInterface
              }
            }
          }
        }
      }
    }
  }
  ${ClickableAnyRewardConfig.fragments.anyRewardConfigInterface}
  ${ClickableAnyRewardConfig.fragments.so5League}
  ${RewardConfigText.fragments.anyRewardConfigInterface}
` as TypedDocumentNode<
  LineupRewardsDistancesQuery,
  LineupRewardsDistancesQueryVariables
>;

type RankingRewardConfig = NonNullable<
  NonNullable<
    LineupRewardsDistancesQuery['so5']['so5LeaderboardContender']
  >['so5Leaderboard']['rewardsConfig']['ranking']
>[number];
type ConditionalRewardConfig = NonNullable<
  NonNullable<
    LineupRewardsDistancesQuery['so5']['so5LeaderboardContender']
  >['so5Leaderboard']['rewardsConfig']['conditional']
>[number];

const isRankingConfig = (
  config: RankingRewardConfig | ConditionalRewardConfig
): config is RankingRewardConfig => {
  return 'fromRank' in config;
};

type Props = {
  so5LeaderboardContenderSlug: string;
  onClose?: () => void;
};

const CompactHorizontal = styled(Horizontal)`
  gap: 2px;
`;

export const LineupRewardsDistances = ({
  so5LeaderboardContenderSlug,
  onClose,
}: Props) => {
  const { formatMessage } = useIntl();
  const { data, loading } = useQuery(LINEUP_REWARDS_DISTANCES_QUERY, {
    variables: { so5LeaderboardContenderSlug },
    fetchPolicy: 'cache-first',
  });

  const { so5LeaderboardContender } = data?.so5 || {};
  const { so5Leaderboard, so5Lineup } = so5LeaderboardContender || {};
  const { rewardsConfig, so5LeaderboardGroup, so5League } =
    so5Leaderboard || {};
  const { so5Rankings } = so5Lineup || {};
  const mySo5Ranking = so5Rankings?.at(0);
  const { percentageOfGamesPlayed } = so5LeaderboardGroup || {};

  const so5RewardConfigs = useMemo(() => {
    if (!so5LeaderboardContender || !mySo5Ranking) {
      return [];
    }

    return [
      ...(rewardsConfig?.ranking || []),
      ...(rewardsConfig?.conditional || []),
    ]
      .map(r => {
        const isRankingRewardConfig = isRankingConfig(r);
        const requiredScore = isRankingRewardConfig
          ? r.toSo5Ranking?.score || 0
          : r.score || 0;
        const distance = mySo5Ranking.score - requiredScore;
        const eligible =
          isRankingRewardConfig &&
          (r.fromRank || 0) <= (mySo5Ranking.ranking || 0) &&
          (mySo5Ranking.ranking || 0) <= (r.toRank || 0);

        let label: ReactNode;

        if (isRankingRewardConfig) {
          label =
            r.fromRank !== r.toRank
              ? `${r.fromRank} - ${r.toRank}`
              : r.fromRank;
        } else {
          label = formatMessage(fantasy.points, { score: r.score });
        }

        const distanceLabel = (
          <LineupRewardsDistanceLabel
            distance={distance}
            requiredScore={requiredScore}
            withFromAppendix
          />
        );

        return {
          ...r,
          distance,
          eligible,
          distanceLabel,
          label,
        };
      })
      .sort((a, b) => a.distance - b.distance);
  }, [
    formatMessage,
    mySo5Ranking,
    rewardsConfig?.conditional,
    rewardsConfig?.ranking,
    so5LeaderboardContender,
  ]);

  return (
    <Root>
      <StyledSBHorizontal>
        <PercentageOfGamesPlayed>
          <FormattedMessage
            defaultMessage="{percentage, number, percent} of games played"
            values={{
              percentage: percentageOfGamesPlayed || ' ',
            }}
          />
        </PercentageOfGamesPlayed>
        <StyledIconButton
          size="small"
          color="transparent"
          icon={iconChevronDown}
          onClick={onClose}
        />
      </StyledSBHorizontal>
      {!!(loading || !data) && (
        <Loader>
          <LoadingIndicator small />
        </Loader>
      )}
      {so5RewardConfigs.map((so5RewardConfig, i) => {
        return (
          <RewardConfig
            // oxlint-disable-next-line react/no-array-index-key
            key={i}
            className={classnames({ eligible: so5RewardConfig.eligible })}
          >
            <Vertical gap={0}>
              {percentageOfGamesPlayed !== undefined &&
                so5RewardConfig.distanceLabel}
              <LabelL as="div" bold>
                {so5RewardConfig.label}
              </LabelL>
            </Vertical>
            <Horizontal gap={0.5}>
              {so5RewardConfig.rewardConfigs.map((rewardConfig, index) => {
                const boostedReward =
                  so5RewardConfig.eligible &&
                  rewardConfig.boostedRewardConfigs
                    ?.filter(
                      ({ __typename }) => __typename === rewardConfig.__typename
                    )
                    .at(0);
                return (
                  <ClickableAnyRewardConfig
                    // oxlint-disable-next-line react/no-array-index-key
                    key={index}
                    anyRewardConfig={rewardConfig}
                    so5League={so5League}
                    variant={Variant.INLINE}
                    customText={
                      !!boostedReward &&
                      (({ rewardText }) => (
                        <CompactHorizontal as="span">
                          {rewardText}
                          <span
                            className="text-gradient-color"
                            style={
                              {
                                '--text-gradient-color':
                                  'var(--c-gradient-greenYellow)',
                              } as CSSProperties
                            }
                          >
                            +
                            <RewardConfigText anyRewardConfig={boostedReward} />
                          </span>
                        </CompactHorizontal>
                      ))
                    }
                  />
                );
              })}
            </Horizontal>
          </RewardConfig>
        );
      })}
    </Root>
  );
};
